/**
 * TanStack Query hooks for QA Reviews
 *
 * Provides hooks for listing, updating, and deleting QA reviews
 * with optimistic updates for instant checkbox toggling.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { qaReviewService } from '../services/qaReviewService';
import type {
  QAReviewFilters,
  QAReviewUpsert,
  QAReviewListResponse,
  QAReviewWithContent,
} from '../types/qaReview';

// Query keys for cache management
export const qaReviewKeys = {
  all: ['qa-reviews'] as const,
  lists: () => [...qaReviewKeys.all, 'list'] as const,
  list: (tenantId: string, filters?: QAReviewFilters) =>
    [...qaReviewKeys.lists(), tenantId, filters] as const,
  details: () => [...qaReviewKeys.all, 'detail'] as const,
  detail: (contentId: string) => [...qaReviewKeys.details(), contentId] as const,
};

/**
 * List QA reviews for a tenant with filtering and sorting.
 * Joins blog_content metadata (keyword, title, status, wordpress_url).
 */
export function useQAReviews(tenantId: string, filters: QAReviewFilters = {}) {
  return useQuery<QAReviewListResponse>({
    queryKey: qaReviewKeys.list(tenantId, filters),
    queryFn: () => qaReviewService.list(tenantId, filters),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Get a single QA review by content ID.
 * Pass { enabled: false } to skip fetching (e.g. when using batch data).
 */
export function useQAReview(contentId: string, tenantId: string, options?: { enabled?: boolean }) {
  const shouldFetch = options?.enabled !== false;
  return useQuery<QAReviewWithContent>({
    queryKey: qaReviewKeys.detail(contentId),
    queryFn: () => qaReviewService.get(contentId, tenantId),
    enabled: shouldFetch && !!contentId && !!tenantId,
    staleTime: 60_000,
  });
}

/**
 * Upsert (create or update) a QA review with optimistic updates.
 * Designed for instant checkbox toggling — updates the cache immediately
 * before the server responds, then reconciles on success/error.
 */
export function useUpdateQAReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, tenantId, data }: { contentId: string; tenantId: string; data: QAReviewUpsert }) =>
      qaReviewService.upsert(contentId, tenantId, data),

    onMutate: async ({ contentId, data }) => {
      // Cancel outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: qaReviewKeys.lists() });

      // Snapshot previous list caches for rollback
      const previousLists = queryClient.getQueriesData<QAReviewListResponse>({
        queryKey: qaReviewKeys.lists(),
      });

      // Optimistically update all list caches that contain this review
      queryClient.setQueriesData<QAReviewListResponse>(
        { queryKey: qaReviewKeys.lists() },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            reviews: old.reviews.map((review) =>
              review.content_id === contentId
                ? { ...review, ...data, updated_at: new Date().toISOString() }
                : review
            ),
          };
        }
      );

      return { previousLists };
    },

    onError: (_error, _variables, context) => {
      // Roll back optimistic update on failure
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          if (data) {
            queryClient.setQueryData(queryKey, data);
          }
        }
      }
      message.error('Failed to update QA review');
    },

    onSettled: () => {
      // Refetch to ensure server state is accurate
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },
  });
}

/**
 * Bulk mark selected reviews as reviewed.
 */
export function useBulkMarkReviewed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentIds, tenantId, reviewedBy }: { contentIds: string[]; tenantId: string; reviewedBy: string }) =>
      qaReviewService.bulkMarkReviewed(contentIds, tenantId, reviewedBy),

    onSuccess: (result) => {
      if (result.failed === 0) {
        message.success(`${result.succeeded} reviews marked as reviewed`);
      } else {
        message.warning(`${result.succeeded} marked, ${result.failed} failed`);
      }
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },

    onError: () => {
      message.error('Failed to bulk mark as reviewed');
    },
  });
}

/**
 * Bulk clear all issue flags for selected reviews.
 */
export function useBulkClearIssues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentIds, tenantId }: { contentIds: string[]; tenantId: string }) => qaReviewService.bulkClearIssues(contentIds, tenantId),

    onSuccess: (result) => {
      if (result.failed === 0) {
        message.success(`Issues cleared for ${result.succeeded} reviews`);
      } else {
        message.warning(`${result.succeeded} cleared, ${result.failed} failed`);
      }
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
    },

    onError: () => {
      message.error('Failed to bulk clear issues');
    },
  });
}

/**
 * Delete/reset a QA review.
 */
export function useDeleteQAReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contentId, tenantId }: { contentId: string; tenantId: string }) => qaReviewService.delete(contentId, tenantId),

    onSuccess: (_, { contentId }) => {
      message.success('QA review reset');
      queryClient.invalidateQueries({ queryKey: qaReviewKeys.lists() });
      queryClient.removeQueries({ queryKey: qaReviewKeys.detail(contentId) });
    },

    onError: () => {
      message.error('Failed to reset QA review');
    },
  });
}
